// ============================================================================
// IssueMap ViewModel
// ============================================================================

import React, { useMemo } from "react";
import { useMapsLibrary, AdvancedMarker } from "@vis.gl/react-google-maps";
import { Polygon, Polyline } from "../../../../../../JobsDashboard/map";
import type { Point, Polygon as GeoJSONPolygon, MultiPolygon } from "geojson";
import type { IssueMapProps, IssueMapViewModel, FeatureMetadata } from "./IssueMap.types";
import {
	getJobBoundaryGeometry,
	extractPathsFromGeometry,
	getAllCoordinatesFromGeometry,
	generateHatchLines,
} from "./IssueMap.helpers";
import {
	BOUNDARY_COLORS,
	NEARBY_JOB_COLOR,
	HATCH_ANGLES,
	HATCH_LINE_SPACING_METERS,
	UNMATCHED_POINT_COLOR,
	UNMATCHED_ZONE_COLOR,
	DEFAULT_CENTER,
	SELECTED_COLOR,
	SELECTED_OPACITY,
} from "../../shared/constants";

/**
 * ViewModel hook for IssueMap component
 * Handles all business logic for processing jobs and generating map elements
 */
export function useIssueMapViewModel(props: IssueMapProps): IssueMapViewModel {
	const {
		jobs,
		visualizationMode,
		highlightGeometry,
		nearbyJobs,
		hiddenJobIds,
		unmatchedFeatures,
		hideUnmatchedFeatures = false,
		hiddenNearbyJobIds,
		duplicateFeatures,
		hiddenDuplicateFeatureIds,
		selectedFeatureIds,
		lassoMode,
	} = props;

	const mapsLibrary = useMapsLibrary("maps");

	// Process job boundaries into polygon elements
	const { polygonElements, markerElements, allCoordinates, featureMetadata } = useMemo(() => {
		const polygons: React.ReactElement[] = [];
		const markers: React.ReactElement[] = [];
		const coords: google.maps.LatLngLiteral[] = [];
		const metadata: FeatureMetadata[] = [];

		// For duplicate-features mode, use only hatch lines (no solid fill)
		// because the boundaries are completely identical and solid fills look strange
		const isDuplicateFeaturesMode = visualizationMode === "duplicate-features";

		// Render main job boundaries
		jobs.forEach((job, index) => {
			// Skip hidden jobs but keep color index consistent
			const isHidden = hiddenJobIds?.includes(job.id);
			const boundaryGeom = getJobBoundaryGeometry(job);

			if (boundaryGeom) {
				const paths = extractPathsFromGeometry(boundaryGeom);
				const color = BOUNDARY_COLORS[index % BOUNDARY_COLORS.length];

				// Always collect coordinates for bounds (so map shows full extent)
				coords.push(...getAllCoordinatesFromGeometry(boundaryGeom));

				// Only render if not hidden
				if (!isHidden) {
					// For duplicate-features mode, skip the filled polygon entirely
					// and just render the outline as a Polyline + hatch lines.
					// This avoids Google Maps Z-order issue where Polygons cover Polylines.
					if (isDuplicateFeaturesMode) {
						// Render outline only (as Polyline, not Polygon)
						paths.forEach((path, pathIndex) => {
							// Close the path by adding first point at the end
							const closedPath = [...path, path[0]];
							polygons.push(
								React.createElement(Polyline, {
									key: `outline-${job.id}-${pathIndex}`,
									path: closedPath,
									strokeColor: color,
									strokeWeight: 3,
									strokeOpacity: 1,
								}),
							);
						});
					} else {
						// Normal mode: render filled polygon
						paths.forEach((path, pathIndex) => {
							polygons.push(
								React.createElement(Polygon, {
									key: `job-${job.id}-${pathIndex}`,
									paths: path,
									fillColor: color,
									fillOpacity: 0.4,
									strokeColor: color,
									strokeWeight: 3,
									strokeOpacity: 1,
								}),
							);
						});
					}

					// Generate and render hatch lines across the full boundary
					const angle = HATCH_ANGLES[index % HATCH_ANGLES.length];
					const hatchLines = generateHatchLines(boundaryGeom, angle, HATCH_LINE_SPACING_METERS);
					hatchLines.forEach((linePath, lineIndex) => {
						polygons.push(
							React.createElement(Polyline, {
								key: `hatch-${job.id}-${lineIndex}`,
								path: linePath,
								strokeColor: color,
								strokeWeight: 1.5,
								strokeOpacity: 0.8,
							}),
						);
					});
				}
			}
		});

		// Render nearby job boundaries (grayed out)
		nearbyJobs?.forEach((nearby) => {
			const isHidden = hiddenNearbyJobIds?.includes(nearby.job.id);
			const boundaryGeom = getJobBoundaryGeometry(nearby.job);

			if (boundaryGeom) {
				const paths = extractPathsFromGeometry(boundaryGeom);

				// Always collect coordinates for bounds (so map shows full extent)
				coords.push(...getAllCoordinatesFromGeometry(boundaryGeom));

				// Only render if not hidden
				if (!isHidden) {
					paths.forEach((path, pathIndex) => {
						polygons.push(
							React.createElement(Polygon, {
								key: `nearby-${nearby.job.id}-${pathIndex}`,
								paths: path,
								fillColor: NEARBY_JOB_COLOR,
								fillOpacity: 0.2,
								strokeColor: NEARBY_JOB_COLOR,
								strokeWeight: 2,
								strokeOpacity: 0.5,
							}),
						);
					});
				}
			}
		});

		// Render unmatched features (points and zones)
		if (unmatchedFeatures?.features) {
			unmatchedFeatures.features.forEach((feature, index) => {
				const geom = feature.geometry;
				if (!geom) return;

				if (geom.type === "Point") {
					const point = geom as Point;
					const position = { lat: point.coordinates[1], lng: point.coordinates[0] };
					const featureId = `unmatched-point-${index}`;
					const isSelected = selectedFeatureIds?.has(featureId);

					// Always collect coordinates for bounds
					coords.push(position);
					// Track metadata for lasso selection
					metadata.push({ id: featureId, paths: [position] });

					// Only render if not hidden
					if (!hideUnmatchedFeatures) {
						markers.push(
							React.createElement(
								AdvancedMarker,
								{ key: featureId, position },
								React.createElement("div", {
									style: {
										width: 14,
										height: 14,
										backgroundColor: isSelected ? SELECTED_COLOR : UNMATCHED_POINT_COLOR,
										borderRadius: "50%",
										border: isSelected ? "3px solid white" : "2px solid white",
										boxShadow: isSelected ? "0 2px 8px rgba(255,0,0,0.5)" : "0 2px 4px rgba(0,0,0,0.3)",
									},
								}),
							),
						);
					}
				} else if (geom.type === "Polygon" || geom.type === "MultiPolygon") {
					const paths = extractPathsFromGeometry(geom as GeoJSONPolygon | MultiPolygon);
					// Always collect coordinates for bounds
					coords.push(...getAllCoordinatesFromGeometry(geom as GeoJSONPolygon | MultiPolygon));

					// Only render if not hidden
					if (!hideUnmatchedFeatures) {
						paths.forEach((path, pathIndex) => {
							const featureId = `unmatched-zone-${index}-${pathIndex}`;
							const isSelected = selectedFeatureIds?.has(featureId);

							// Track metadata for lasso selection
							metadata.push({ id: featureId, paths: path });

							polygons.push(
								React.createElement(Polygon, {
									key: featureId,
									paths: path,
									fillColor: isSelected ? SELECTED_COLOR : UNMATCHED_ZONE_COLOR,
									fillOpacity: isSelected ? SELECTED_OPACITY : 0.4,
									strokeColor: isSelected ? SELECTED_COLOR : UNMATCHED_ZONE_COLOR,
									strokeWeight: isSelected ? 4 : 3,
									strokeOpacity: 1,
									clickable: !lassoMode,
								}),
							);
						});
					}
				}
			});
		}

		// Render duplicate features (points and zones)
		if (duplicateFeatures) {
			// Sort by size descending so largest points render first (on bottom)
			// This ensures proper z-order regardless of visibility toggle order
			const sortedFeatures = [...duplicateFeatures].sort((a, b) => (b.size ?? 14) - (a.size ?? 14));

			sortedFeatures.forEach((featureData) => {
				const { feature, color, size, groupId, featureIndex } = featureData;
				const featureId = `${groupId}-${featureIndex}`;
				const isHidden = hiddenDuplicateFeatureIds?.has(featureId);

				if (isHidden) return;

				const geom = feature.geometry;
				if (!geom) return;

				if (geom.type === "Point") {
					const point = geom as Point;
					const position = { lat: point.coordinates[1], lng: point.coordinates[0] };
					coords.push(position);

					// Calculate zIndex: smaller points get higher zIndex so they appear on top
					// This ensures proper stacking regardless of visibility toggle order
					const markerSize = size || 14;
					const zIndex = 100 - markerSize; // e.g., size 6 → zIndex 94, size 24 → zIndex 76

					markers.push(
						React.createElement(
							AdvancedMarker,
							{ key: `duplicate-${featureId}`, position, zIndex },
							React.createElement("div", {
								style: {
									width: size || 14,
									height: size || 14,
									backgroundColor: color,
									borderRadius: "50%",
									border: "2px solid white",
									boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
								},
							}),
						),
					);
				} else if (geom.type === "Polygon" || geom.type === "MultiPolygon") {
					// Handle zones similar to unmatched zones
					const paths = extractPathsFromGeometry(geom as GeoJSONPolygon | MultiPolygon);
					coords.push(...getAllCoordinatesFromGeometry(geom as GeoJSONPolygon | MultiPolygon));

					paths.forEach((path, pathIndex) => {
						polygons.push(
							React.createElement(Polygon, {
								key: `duplicate-${featureId}-${pathIndex}`,
								paths: path,
								fillColor: color,
								fillOpacity: 0.4,
								strokeColor: color,
								strokeWeight: 3,
								strokeOpacity: 1,
							}),
						);
					});
				}
			});
		}

		// Render highlight geometry (e.g., intersection area) LAST so it renders on top
		if (highlightGeometry?.geometry) {
			const geom = highlightGeometry.geometry;
			if (geom.type === "Polygon" || geom.type === "MultiPolygon") {
				const highlightGeom = geom as GeoJSONPolygon | MultiPolygon;

				// Collect coordinates for bounds
				coords.push(...getAllCoordinatesFromGeometry(highlightGeom));

				// Hatch lines for each job already cover their full boundary (including the overlap area),
				// so overlapping jobs naturally cross-hatch here — no extra rendering needed.
			}
		}

		return { polygonElements: polygons, markerElements: markers, allCoordinates: coords, featureMetadata: metadata };
	}, [
		jobs,
		visualizationMode,
		nearbyJobs,
		highlightGeometry,
		hiddenJobIds,
		unmatchedFeatures,
		hideUnmatchedFeatures,
		hiddenNearbyJobIds,
		duplicateFeatures,
		hiddenDuplicateFeatureIds,
		selectedFeatureIds,
		lassoMode,
	]);

	// Calculate center from coordinates
	const center = useMemo(() => {
		if (allCoordinates.length === 0) return DEFAULT_CENTER;

		const sumLat = allCoordinates.reduce((sum, c) => sum + c.lat, 0);
		const sumLng = allCoordinates.reduce((sum, c) => sum + c.lng, 0);

		return {
			lat: sumLat / allCoordinates.length,
			lng: sumLng / allCoordinates.length,
		};
	}, [allCoordinates]);

	return {
		polygonElements,
		markerElements,
		allCoordinates,
		center,
		isLoading: !mapsLibrary,
		featureMetadata,
	};
}
